// baseDatabaseDisplay.js

import { getPluralLabel } from 'utilities/Languages';

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeTimestamp(value) {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value?.seconds === 'number') return value.seconds;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  return 0;
}

function humanizeStudioAppKey(studioAppKey) {
  const normalized = normalizeString(studioAppKey);
  if (!normalized) return '';

  return normalized
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * @param {object|null} typeDef
 * @param {object|null} database
 * @returns {object}
 */
export function getLibrarySource(typeDef = null, database = null) {
  if (typeDef?.librarySource && typeof typeDef.librarySource === 'object') {
    return typeDef.librarySource;
  }

  if (database?.librarySource && typeof database.librarySource === 'object') {
    return database.librarySource;
  }

  return {};
}

/**
 * Returns true when a node type was provisioned from an app install (Store or Studio).
 *
 * @param {object|null} typeDef
 * @param {object|null} [database]
 * @returns {boolean}
 */
export function isAppProvisionedType(typeDef, database = null) {
  const librarySource = getLibrarySource(typeDef, database);
  if (!librarySource || typeof librarySource !== 'object') return false;

  if (normalizeString(librarySource.instanceKey)) return true;
  if (normalizeString(librarySource.storeAppKey)) return true;
  if (normalizeString(librarySource.termKey) && normalizeString(librarySource.addonKey)) return true;
  if (normalizeString(librarySource.source) === 'studio' && normalizeString(librarySource.studioAppKey)) {
    return true;
  }

  return false;
}

/**
 * @param {object|null} typeDef
 * @param {object|null} [database]
 * @returns {string}
 */
export function getAppDisplayTitleFromTypeDef(typeDef, database = null) {
  const librarySource = getLibrarySource(typeDef, database);
  if (!librarySource || typeof librarySource !== 'object') return '';

  return normalizeString(librarySource.studioAppTitle)
    || humanizeStudioAppKey(librarySource.studioAppKey)
    || normalizeString(librarySource.storeAppTitle)
    || normalizeString(librarySource.storeAddonTitle)
    || normalizeString(librarySource.storeTermLabel)
    || normalizeString(librarySource.termKey)
    || '';
}

/**
 * Picks the canonical row when duplicate Studio installs share a blueprint database.
 *
 * @param {object} left
 * @param {object} right
 * @returns {boolean}
 */
export function preferAppInstanceEntry(left, right) {
  const leftTitle = getAppDisplayTitleFromTypeDef(left?.typeDef, left?.database);
  const rightTitle = getAppDisplayTitleFromTypeDef(right?.typeDef, right?.database);

  if (leftTitle && !rightTitle) return true;
  if (!leftTitle && rightTitle) return false;

  const leftModified = normalizeTimestamp(left?.database?.dateModified || left?.typeDef?.dateModified);
  const rightModified = normalizeTimestamp(right?.database?.dateModified || right?.typeDef?.dateModified);
  if (leftModified !== rightModified) return leftModified > rightModified;

  return normalizeString(left?.database?.key) > normalizeString(right?.database?.key);
}

/**
 * @param {array} entries
 * @returns {object|null}
 */
export function pickPreferredAppInstanceEntry(entries = []) {
  return (Array.isArray(entries) ? entries : []).reduce((preferred, entry) => {
    if (!preferred) return entry;
    return preferAppInstanceEntry(entry, preferred) ? entry : preferred;
  }, null);
}

/**
 * Collapses duplicate databases from repeated installs of the same Studio app blueprint.
 *
 * @param {array} entries
 * @returns {array}
 */
export function dedupeAppInstanceEntries(entries = []) {
  const cleanedEntries = Array.isArray(entries) ? entries : [];
  if (cleanedEntries.length <= 1) return cleanedEntries;

  const byBlueprintKey = new Map();

  cleanedEntries.forEach((entry) => {
    const librarySource = getLibrarySource(entry?.typeDef, entry?.database);
    const blueprintKey = normalizeString(librarySource.studioDatabaseKey)
      || normalizeString(entry?.database?.key)
      || normalizeString(entry?.typeDef?.key);

    if (!blueprintKey) return;

    const existing = byBlueprintKey.get(blueprintKey);
    if (!existing || preferAppInstanceEntry(entry, existing)) {
      byBlueprintKey.set(blueprintKey, entry);
    }
  });

  if (!byBlueprintKey.size) return cleanedEntries;

  return Array.from(byBlueprintKey.values());
}

/**
 * Groups installed app databases under a stable app-instance key.
 *
 * Studio installs group by `studioAppKey` so repeat installs do not create sibling app folders.
 *
 * @param {object|null} typeDef
 * @param {object|null} [database]
 * @returns {string}
 */
export function resolveAppInstanceGroupKey(typeDef, database = null) {
  const librarySource = getLibrarySource(typeDef, database);

  const instanceKey = normalizeString(librarySource.instanceKey);
  if (instanceKey) return `instance:${instanceKey}`;

  const studioAppKey = normalizeString(librarySource.studioAppKey);
  if (normalizeString(librarySource.source) === 'studio' && studioAppKey) {
    return `studio-app:${studioAppKey}`;
  }

  const termKey = normalizeString(librarySource.termKey);
  const addonKey = normalizeString(librarySource.addonKey);
  if (termKey && addonKey) return `${termKey}::${addonKey}`;

  const typeKey = normalizeString(database?.type || typeDef?.key);
  return typeKey ? `type:${typeKey}` : '';
}

/**
 * Builds a user-facing database label. Prefixes with the app title in flat contexts
 * when the database belongs to an installed app instance.
 *
 * @param {object} params
 * @param {string} params.baseLabel
 * @param {object|null} [params.typeDef]
 * @param {object|null} [params.database]
 * @param {boolean} [params.nestedUnderApp=false]
 * @returns {string}
 */
export function buildDatabaseDisplayLabel({
  baseLabel,
  typeDef = null,
  database = null,
  nestedUnderApp = false,
}) {
  const normalizedBaseLabel = normalizeString(baseLabel) || 'Database';
  if (nestedUnderApp || !isAppProvisionedType(typeDef, database)) {
    return normalizedBaseLabel;
  }

  const appTitle = getAppDisplayTitleFromTypeDef(typeDef, database);
  if (!appTitle) return normalizedBaseLabel;

  return `${appTitle} · ${normalizedBaseLabel}`;
}

/**
 * Resolves the base database title before app-name qualification.
 *
 * @param {object} [params]
 * @param {object|null} [params.database]
 * @param {object|null} [params.typeDef]
 * @param {string} [params.language]
 * @returns {string}
 */
export function resolveDatabaseBaseDisplayLabel({
  database = null,
  language = 'en',
  typeDef = null,
} = {}) {
  const typeKey = normalizeString(database?.type || typeDef?.key);
  const rawTitle = normalizeString(database?.title);
  const databaseKey = normalizeString(database?.key);
  const isTypeKeyPlaceholderTitle = rawTitle === normalizeString(typeKey);

  if (rawTitle && rawTitle !== databaseKey && !isTypeKeyPlaceholderTitle) {
    return rawTitle;
  }

  return typeKey
    ? (getPluralLabel(typeDef, typeKey, language) || 'Database')
    : 'Database';
}

/**
 * Builds a qualified database label for flat base surfaces.
 *
 * @param {object} [params]
 * @param {object|null} [params.database]
 * @param {object|null} [params.typeDef]
 * @param {string} [params.language]
 * @param {boolean} [params.nestedUnderApp]
 * @returns {string}
 */
export function buildQualifiedDatabaseDisplayLabel({
  database = null,
  language = 'en',
  nestedUnderApp = false,
  typeDef = null,
} = {}) {
  return buildDatabaseDisplayLabel({
    baseLabel: resolveDatabaseBaseDisplayLabel({ database, language, typeDef }),
    database,
    nestedUnderApp,
    typeDef,
  });
}

/**
 * @param {object|null} typeDef
 * @param {object|null} [database]
 * @returns {string}
 */
export function resolvePersistedInstanceKey(typeDef, database = null) {
  return normalizeString(getLibrarySource(typeDef, database).instanceKey);
}
